import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Loader2,
  Mail,
  ShoppingCart,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  TrendingUp,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, formatMXN } from '../lib/api'

interface CartItem {
  productId?: string
  name?: string
  quantity?: number
  unitPrice?: number
}

interface AbandonedCart {
  id: string
  email: string
  customerName: string
  items: CartItem[]
  itemsCount: number
  subtotal: number
  cartUrl: string
  remindedAt: string | null
  recoveredAt: string | null
  isRecovered: boolean
  createdAt: string
  updatedAt: string
  status: 'pending' | 'reminded' | 'recovered'
}

interface AbandonedCartsResponse {
  total: number
  page: number
  limit: number
  pages: number
  stats: {
    pending: number
    reminded: number
    recovered: number
    totalAll: number
    recoveryRate: number
  }
  data: AbandonedCart[]
}

type StatusFilter = 'all' | 'pending' | 'reminded' | 'recovered'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'reminded', label: 'Recordados' },
  { value: 'recovered', label: 'Recuperados' },
]

function daysAgo(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const diffMs = Date.now() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  if (diffDays >= 1) return `hace ${diffDays} día${diffDays === 1 ? '' : 's'}`
  if (diffHours >= 1) return `hace ${diffHours} h`
  const diffMin = Math.max(1, Math.floor(diffMs / (1000 * 60)))
  return `hace ${diffMin} min`
}

function StatusBadge({ status }: { status: AbandonedCart['status'] }) {
  if (status === 'recovered') return <Badge variant="success">Recuperado</Badge>
  if (status === 'reminded') return <Badge variant="info">Recordado</Badge>
  return <Badge variant="warning">Pendiente</Badge>
}

export function CarritosAbandonados() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3500)
  }

  const queryUrl = (() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', '25')
    if (status !== 'all') params.set('status', status)
    if (searchTerm) params.set('q', searchTerm)
    return `/api/admin/carritos-abandonados?${params.toString()}`
  })()

  const { data, isLoading, isFetching } = useQuery<AbandonedCartsResponse>({
    queryKey: ['carritos-abandonados', page, status, searchTerm],
    queryFn: () => apiGet(queryUrl),
    placeholderData: (prev) => prev,
  })

  const sendNowMut = useMutation({
    mutationFn: (id: string) =>
      apiPost<{ ok: true }>(`/api/admin/carritos-abandonados/${id}/send-now`),
    onSuccess: () => {
      notify('ok', 'Email de recuperación enviado')
      qc.invalidateQueries({ queryKey: ['carritos-abandonados'] })
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  const subtitle = data
    ? `${data.stats.totalAll.toLocaleString('es-MX')} carritos · ${data.stats.recoveryRate}% de recuperación`
    : 'Cargando…'

  return (
    <>
      <Topbar title="Carritos abandonados" subtitle={subtitle} />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {/* Stats */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Pendientes</div>
              <div className="grid size-9 place-items-center rounded-lg bg-amber-50 text-amber-600">
                <Clock className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data ? data.stats.pending.toLocaleString('es-MX') : '—'}
            </div>
            <div className="mt-1 text-[11px] text-ink-500">Sin recordatorio enviado</div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Recordados</div>
              <div className="grid size-9 place-items-center rounded-lg bg-blue-50 text-blue-600">
                <Mail className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data ? data.stats.reminded.toLocaleString('es-MX') : '—'}
            </div>
            <div className="mt-1 text-[11px] text-ink-500">Email enviado, sin compra</div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Recuperados</div>
              <div className="grid size-9 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
                <ShoppingCart className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data ? data.stats.recovered.toLocaleString('es-MX') : '—'}
            </div>
            <div className="mt-1 text-[11px] text-ink-500">Volvieron al checkout</div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Tasa de recuperación</div>
              <div className="grid size-9 place-items-center rounded-lg bg-violet-50 text-violet-600">
                <TrendingUp className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data ? `${data.stats.recoveryRate}%` : '—'}
            </div>
            <div className="mt-1 text-[11px] text-ink-500">Recuperados / Recordados</div>
          </div>
        </div>

        {/* Filtros */}
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por email o nombre…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
          <div className="ml-2 inline-flex overflow-hidden rounded-lg border border-ink-200 bg-white text-sm">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  setStatus(o.value)
                  setPage(1)
                }}
                className={`px-3 py-2 font-medium transition-colors ${
                  status === o.value
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </form>

        {/* Tabla */}
        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando carritos…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Email / Cliente</th>
                      <th className="px-4 py-3 text-center font-medium">Items</th>
                      <th className="px-4 py-3 text-right font-medium">Subtotal</th>
                      <th className="px-4 py-3 font-medium">Abandonado</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="w-36 px-4 py-3 text-right font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink-900">{c.email}</div>
                          {c.customerName && (
                            <div className="text-xs text-ink-500">{c.customerName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-ink-700">
                          {c.itemsCount.toLocaleString('es-MX')}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-ink-900">
                          {formatMXN(c.subtotal)}
                        </td>
                        <td className="px-4 py-3 text-xs text-ink-600">
                          {daysAgo(c.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={c.status} />
                          {c.remindedAt && c.status !== 'recovered' && (
                            <div className="mt-1 text-[10px] text-ink-500">
                              Recordado {daysAgo(c.remindedAt)}
                            </div>
                          )}
                          {c.recoveredAt && (
                            <div className="mt-1 text-[10px] text-emerald-600">
                              Recuperado {daysAgo(c.recoveredAt)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            {c.status !== 'recovered' && (
                              <button
                                type="button"
                                title={c.remindedAt ? 'Reenviar email' : 'Enviar email ahora'}
                                disabled={sendNowMut.isPending}
                                onClick={() => {
                                  const msg = c.remindedAt
                                    ? `Reenviar email de recuperación a ${c.email}?`
                                    : `Enviar email de recuperación a ${c.email}?`
                                  if (confirm(msg)) sendNowMut.mutate(c.id)
                                }}
                                className="inline-flex items-center gap-1 rounded border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-50"
                              >
                                {sendNowMut.isPending && sendNowMut.variables === c.id ? (
                                  <Loader2 className="size-3 animate-spin" />
                                ) : (
                                  <Send className="size-3" />
                                )}
                                Enviar ahora
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-ink-500">
                          Sin carritos abandonados con esos filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {data.pages > 1 && (
                <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                  <div className="text-ink-500">
                    Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                    <strong className="text-ink-900">
                      {data.pages.toLocaleString('es-MX')}
                    </strong>{' '}
                    ({data.total.toLocaleString('es-MX')} resultados)
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button className="rounded-md bg-brand-600 px-3 py-1 text-white">
                      {page}
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= data.pages}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
